import { Component, Element, Event, EventEmitter, Host, Prop, State, Watch, h } from '@stencil/core';
import { getAPI, type UserAPI } from '@sydle/ui-api';
import { Consumer } from '@sydle/ui-utils';
import { BlogPost, type BlogAuthor } from '../../../types/blog';
import { richContentEvent } from '../../../utils/tracking-utils';
import { getAssetsURL } from '../../../utils/utils';

@Component({
  tag: 'sy-blog-post-header',
  styleUrl: 'scss/index.scss',
  shadow: true,
})
export class SyBlogPostHeader {
  private consumer: Consumer;
  @Element() el: HTMLSyBlogPostHeaderElement;

  /**
   * Post body
   */
  @Prop() postBody: BlogPost;

  /**
   * Tag to indentify restricted post
   */
  @Prop() tagRestrictedPost: string;

  /**
   * Text to access restricted post
   */
  @Prop() restrictedPostAccessButtonLabel: string;

  /**
   * Text of download restricted post
   */
  @Prop() restrictedBlogDownloadButtonLabel: string;

  /**
   * Last update date prefix
   */
  @Prop() lastUpdateDatePrefix: string;

  @State() buttonLoading = false;

  private imageAspect: string | number;
  private ctaText: string;
  private ctaTarget: '_blank' | '_self' = '_blank';
  private author: BlogAuthor;

  @Watch('postBody')
  postBodyWatcher(): void {
    if (this.postBody) {
      if (getAPI<UserAPI>('userAPI')?.getUser() && sessionStorage.getItem('downloadLogin')) {
        sessionStorage.removeItem('downloadLogin');
        this.downloadItem();
      }
    }
  }

  /**
   * Emitted after CTA button is clicked
   */
  @Event({ bubbles: true, composed: true }) sySignInWallOpen: EventEmitter<void>;

  private async downloadItem() {
    const lang = getAPI<UserAPI>('userAPI')?.getCurrentLanguage() || 'en';
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style.display = 'none';
    a.download = this.postBody.title.replace(/\./g, '_');
    a.href = await window.blogFacade
      ?.downloadRichContent({ postId: this.postBody._id, lang: lang })
      .then((response) => {
        if (response.error) {
          console.error(response.error);
        }
        this.buttonLoading = false;
        return URL.createObjectURL(response.data as Blob);
      });
    a.click();

    richContentEvent(this.postBody);
  }

  /**
   * Manipula o clique no botão de chamada à ação (CTA).
   * @param event O evento de clique.
   */
  private ctaClickHandler(event: Event) {
    if (!getAPI<UserAPI>('userAPI')?.getUser()) {
      this.postBody.attachment && sessionStorage.setItem('downloadLogin', 'true');
      this.sySignInWallOpen.emit();
      return;
    }
    const button = event.target as HTMLSyButtonElement;
    this.buttonLoading = true;
    this.downloadItem();
    button.blur();
  }

  /**
   * Método lifecycle que é chamado antes da renderização.
   * Prepara os dados do post para exibição.
   */
  componentWillRender() {
    if (this.postBody) {
      this.imageAspect = this.postBody.img?.dimensions?.width / this.postBody.img?.dimensions?.height || 16 / 9;

      this.author =
        typeof this.postBody.author === 'string'
          ? JSON.parse(this.postBody.author)
          : this.postBody.author ?? this.author;
      this.ctaText =
        this.postBody.attachment !== undefined
          ? this.restrictedBlogDownloadButtonLabel
          : getAPI<UserAPI>('userAPI')?.getUser()
          ? ''
          : this.restrictedPostAccessButtonLabel;
    }
  }

  connectedCallback() {
    this.consumer = new Consumer(this.el);
    this.consumer.subscribe('postBody', (post: BlogPost) => (this.postBody = post));
  }

  disconnectedCallback() {
    this.consumer.disconnect();
  }

  render() {
    return !this.postBody ? (
      <Host>
        <header class="grid">
          <div class="info">
            <sy-text class="category skeleton" type="body">
              <sy-blog-skeleton-load />
            </sy-text>
            <sy-text class="title skeleton" type="h2">
              <sy-blog-skeleton-load />
            </sy-text>
            <sy-text class="description skeleton" type="small">
              <sy-blog-skeleton-load media />
            </sy-text>
            <div class="flex-row skeleton">
              <div class="author">
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '999px',
                  }}
                >
                  <sy-blog-skeleton-load></sy-blog-skeleton-load>
                </div>
                <sy-text type="body"></sy-text>
              </div>
              <div class="update-date skeleton">
                <sy-blog-skeleton-load />
              </div>
            </div>
          </div>
          <div class="image skeleton">
            <sy-blog-skeleton-load media />
          </div>
        </header>
      </Host>
    ) : (
      <Host>
        <header class="grid">
          <div class="info">
            <div class="category-section">
              {this.postBody.isRichContent && <sy-tag>{this.tagRestrictedPost}</sy-tag>}
              <a class="category" target="_self" href={this.postBody.category.link}>
                <sy-text type="body">{this.postBody.category.name}</sy-text>
              </a>
            </div>

            <sy-text class="title" type="h1">
              {this.postBody.title}
            </sy-text>

            {this.postBody.description && (
              <sy-text class="description" type="body">
                {this.postBody.description}
              </sy-text>
            )}
            {this.author && this.postBody.date && this.postBody.classification === 'Blog Post' && (
              <div class="flex-row">
                <div class="author">
                  <sy-avatar
                    image={this.author?.photoURL && getAssetsURL(this.author?.photoURL || '')}
                    initials={this.author.name.toUpperCase()[0]}
                    rounded
                    small
                  ></sy-avatar>
                  <sy-text type="body">{this.author.name}</sy-text>
                </div>
                <sy-text class="update-date" type="body">
                  {this.lastUpdateDatePrefix} {this.postBody.date}
                </sy-text>
              </div>
            )}

            {this.ctaText && this.postBody.classification === 'Blog Post (Acesso restrito)' && (
              <sy-button
                onClick={(e) => this.ctaClickHandler(e)}
                loading={this.buttonLoading}
                label={this.ctaText}
                variant="filled"
                color="secondary"
                icon={this.postBody?.attachment !== undefined ? 'download' : undefined}
                content={this.postBody?.attachment !== undefined ? 'icon-right' : 'text-only'}
                target={this.ctaTarget}
              ></sy-button>
            )}
          </div>
          <div class="image">
            <sy-blog-img
              loading="eager"
              max-width={1200}
              srcsetWidths={[720, 1200]}
              source={this.postBody.img?.src}
              alt={this.postBody.title}
              title={this.postBody.title}
              {...(this.imageAspect && { aspect: this.imageAspect })}
              style={{ '--sy-blog-img-width': '100%' }}
            ></sy-blog-img>
          </div>
        </header>
      </Host>
    );
  }
}
